import { type ColumnDef, createColumnHelper, type SortingState } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { CopyIdsButton } from "~/components/copy-button";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { Button, Switch } from "~/components/ui";
import { useEvidencesEditTable } from "~/features/dataImportProjects/hooks/useEvidencesEditTable";
import type { FlattenedDraft } from "~/features/dataImportProjects/types";
import { rootRoute as R } from "~/routes";
import { EvidenceSetExportDialog } from "./EvidenceSetExportDialog";

type Props = {
  evidenceSetId: string;
};

const helper = createColumnHelper<DataTableFeatures, FlattenedDraft>();

export function EvidenceSetEvidencesTable({ evidenceSetId }: Props) {
  const { t } = useTranslation();
  const { pagination, setPagination, flattenedDrafts, columnHeaders, evidencesCount, showAllFields, setShowAllFields } =
    useEvidencesEditTable(evidenceSetId);

  const [exportOpen, setExportOpen] = useState(false);
  // Server pagination only; the evidences query does not support sort, so columns are non-sortable.
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<DataTableFeatures, FlattenedDraft, any>[]>(() => {
    // evidenceId is always the first column; the rest keep their map order.
    const entries = Array.from(columnHeaders).sort(([a], [b]) =>
      a === "evidenceId" ? -1 : b === "evidenceId" ? 1 : 0,
    );

    return entries.map(([key, header]) =>
      helper.accessor((row) => row[key] ?? "", {
        id: key,
        header: () => header,
        enableSorting: false,
        cell:
          key === "evidenceId"
            ? (info) => (
                <Link
                  to={R.evidences.detail.$buildPath({ params: { id: info.row.original.evidenceId } })}
                  className="font-mono text-12-regular text-primary-700 hover:underline"
                >
                  {info.row.original.evidenceId}
                </Link>
              )
            : (info) => (
                <span className="whitespace-pre-wrap break-words text-12-regular text-fg-1">{info.getValue()}</span>
              ),
      }),
    );
  }, [columnHeaders]);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-12-regular text-fg-3">
          {evidencesCount.toLocaleString()} {t("model.evidence.modelNames")}
        </span>
        <div className="flex items-center gap-2 text-12-regular text-fg-2">
          <Switch checked={showAllFields} onCheckedChange={setShowAllFields} aria-label={t("ui.showAllFields")} />
          <span>{t("ui.showAllFields")}</span>
        </div>
        <CopyIdsButton ids={flattenedDrafts.map((row) => row.evidenceId)} className="ml-auto" />
        <Button type="button" variant="outline" size="sm" onClick={() => setExportOpen(true)}>
          <Download className="size-3.5" />
          {t("ui.export")}
        </Button>
      </div>

      <RemoteDataTable<FlattenedDraft>
        columns={columns}
        data={flattenedDrafts}
        rowCount={evidencesCount}
        pageIndex={pagination.pageIndex}
        onPageIndexChange={(next) => setPagination((prev) => ({ ...prev, pageIndex: next }))}
        pageSize={pagination.pageSize}
        onPageSizeChange={(next) => setPagination((prev) => ({ ...prev, pageSize: next, pageIndex: 0 }))}
        sorting={sorting}
        onSortingChange={setSorting}
        emptyMessage={t("feature.common.noData")}
      />

      <EvidenceSetExportDialog evidenceSetId={evidenceSetId} open={exportOpen} onClose={() => setExportOpen(false)} />
    </section>
  );
}
